import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import ErrorBoundary   from './components/ErrorBoundary';
import Layout          from './components/Layout';
import Login           from './pages/Login';
import ForgotPassword  from './pages/ForgotPassword';
import HospitalRegistration from './pages/HospitalRegistration';
import PrivacyPolicy       from './pages/PrivacyPolicy';
import TermsAndConditions  from './pages/TermsAndConditions';
import AdminApp        from './pages/admin/AdminApp';

const Dashboard       = lazy(() => import('./pages/Dashboard'));
const DoctorMgmt      = lazy(() => import('./pages/DoctorManagement'));
const Appointments    = lazy(() => import('./pages/Appointments'));
const Patients        = lazy(() => import('./pages/Patients'));
const Notifications   = lazy(() => import('./pages/Notifications'));
const Reviews         = lazy(() => import('./pages/Reviews'));
const Reports         = lazy(() => import('./pages/Reports'));
const Analytics       = lazy(() => import('./pages/Analytics'));
const Settings        = lazy(() => import('./pages/Settings'));
const StaffManagement = lazy(() => import('./pages/StaffManagement'));
const Settlements     = lazy(() => import('./pages/Settlements'));
const Posts           = lazy(() => import('./pages/Posts'));
const SubmitOffer     = lazy(() => import('./pages/SubmitOffer'));

function PageLoader() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '40vh', color: 'var(--text-muted)', fontSize: 14, gap: 10,
    }}>
      <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⟳</span>
      Loading…
    </div>
  );
}

function Guard({ children }) {
  const { isLoggedIn } = useApp();
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/admin/*" element={<AdminApp />} />

        <Route path="/login"            element={<Login />} />
        <Route path="/forgot-password"  element={<ForgotPassword />} />
        <Route path="/apply"   element={<HospitalRegistration />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms"   element={<TermsAndConditions />} />

        <Route path="/" element={<Guard><Layout /></Guard>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard"        element={<Dashboard />} />
          <Route path="emergency-alerts" element={<Navigate to="/dashboard" replace />} />
          <Route path="beds"             element={<Navigate to="/dashboard" replace />} />
          <Route path="doctors"          element={<DoctorMgmt />} />
          <Route path="appointments"     element={<Appointments />} />
          <Route path="patients"         element={<Patients />} />
          <Route path="notifications"    element={<Notifications />} />
          <Route path="reviews"          element={<Reviews />} />
          <Route path="reports"          element={<Reports />} />
          <Route path="analytics"        element={<Analytics />} />
          <Route path="settings"         element={<Settings />} />
          <Route path="staff"            element={<StaffManagement />} />
          <Route path="settlements"      element={<Settlements />} />
          <Route path="posts"           element={<Posts />} />
          <Route path="submit-offer"    element={<SubmitOffer />} />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AppProvider>
    </ErrorBoundary>
  );
}
